import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { ResizeIcon } from 'common/components/Elements/Icon/Icon';
import theme from 'core/utilities/theme';
import React from 'react';

import classes from './ResizerHandle.style';

export const resizerHandleWidth = 30; // no ratio as the header only adjust its width, but doesn't scale its content

interface ComponentProps {
  /** Callback once the user release the handle */
  onResize: (cursorPositionX: number) => void;
  onStart: () => void;
  left: number;
  show: boolean;
  /** Maximum position of the handle, gives an edge to the possible resize */
  maxLeft?: number;
  /** Minimum position of the handle, gives an edge to the possible resize */
  minLeft?: number;
  zoomLevel: number;
}

function ResizerHandle({
  onResize, left: initialLeft, show, maxLeft: initialMaxLeft, minLeft, zoomLevel, onStart,
}: ComponentProps) {
  const ref = React.createRef<HTMLDivElement>();

  const [dragging, setDragging] = React.useState(false);
  const [maxLeft, setMaxLeft] = React.useState(initialMaxLeft);
  const [left, setLeft] = React.useState(initialLeft);
  const [hover, setHover] = React.useState(false);

  // Store the position between the cursor and the handle when the user start dragging
  const [cursorToHandleDiff, setCursorToHandleDiff] = React.useState(0);

  const handleStartDragging = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setDragging(true);
    setCursorToHandleDiff(e.pageX - window.scrollX - (ref.current?.getBoundingClientRect().left || 0));
    onStart();
  };

  const handleStopDragging = () => {
    if (!dragging) return;
    setDragging(false);
    if (left !== initialLeft) onResize(left * ((200 - zoomLevel) / 100));
  };

  const handleDragging = (e: MouseEvent) => {
    if (dragging) {
      const adjustedPageX = e.pageX - window.scrollX - cursorToHandleDiff;

      if (minLeft !== undefined && adjustedPageX < minLeft) {
        // too small, keep the previous position
      } else if (maxLeft !== undefined) {
        if (adjustedPageX > 0 && adjustedPageX < maxLeft) setLeft(adjustedPageX);
      } else {
        setLeft(adjustedPageX);
      }
    }
  };

  React.useEffect(() => {
    // maxLeft might change if the user resize its screen
    if (!dragging && initialMaxLeft !== maxLeft) {
      if (maxLeft && initialMaxLeft) {
        // Ensure left (the handle) is never over the viewport after resize
        if (left > initialMaxLeft) setLeft(initialMaxLeft);
      }

      setMaxLeft(initialMaxLeft);
    }
  }, [dragging, initialMaxLeft, maxLeft, left]);

  React.useEffect(() => {
    setLeft(initialLeft);
  }, [initialLeft]);

  React.useEffect(() => {
    document.addEventListener('mouseup', handleStopDragging);
    document.addEventListener('mousemove', handleDragging);

    return () => {
      document.removeEventListener('mouseup', handleStopDragging);
      document.removeEventListener('mousemove', handleDragging);
    };
  });

  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute',
        left: `${left}px`,
        color: dragging || hover ? theme.palette.text.primary : theme.palette.text.disabled,
        zIndex: 9999,
      }}
      onMouseDown={(e) => {
        if (e.button === 0) {
          handleStartDragging(e);
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span style={{ visibility: show || hover || dragging ? 'visible' : 'hidden' }}>
        <IconButton
          size="small"
          color="inherit"
        >
          <ResizeIcon color="inherit" />
        </IconButton>
      </span>
      {dragging && <Box sx={classes.line} />}
    </Box>
  );
}

export default ResizerHandle;
